import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SubscriptionBanner = ({ subscription, onUpgrade, onManageBilling }) => {
  const [bannerImage, setBannerImage] = useState(null);
  const fileInputRef = useRef(null);

  const planName = subscription?.subscription_plans?.name || 'Free';
  const tier = subscription?.subscription_plans?.tier || 'free';
  const maxAgents = subscription?.subscription_plans?.max_agents || 2;
  const isFree = tier === 'free';
  const currentPeriodEnd = subscription?.current_period_end;
  const status = subscription?.status || 'active';

  const handleImageUpload = (event) => {
    const file = event?.target?.files?.[0];
    if (file && file?.type?.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setBannerImage(reader?.result);
      };
      reader?.readAsDataURL(file);
    }
  };

  const handleImageClick = () => {
    fileInputRef?.current?.click();
  };

  const getPlanColor = () => {
    switch (tier) {
      case 'draftiq_plus':
        return 'from-blue-500/10 via-blue-500/5';
      case 'enterprise':
        return 'from-purple-500/10 via-purple-500/5';
      default:
        return 'from-gray-500/10 via-gray-500/5';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = () => {
    const badges = {
      active: { text: 'Active', color: 'text-green-500 bg-green-500/10' },
      canceled: { text: 'Canceled', color: 'text-red-500 bg-red-500/10' },
      past_due: { text: 'Past Due', color: 'text-orange-500 bg-orange-500/10' },
      trialing: { text: 'Trial', color: 'text-blue-500 bg-blue-500/10' }
    };
    return badges?.[status] || badges?.active;
  };

  const statusBadge = getStatusBadge();

  return (
    <div
      className={`
        bg-gradient-to-br ${getPlanColor()} to-transparent
        rounded-xl border border-border p-6 relative overflow-hidden
      `}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
      />

      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mt-24 lg:mt-0">
        <div className="flex items-start gap-4 flex-1">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="CreditCard" size={24} color="var(--color-primary)" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-lg font-semibold text-foreground font-heading">
                {planName} Plan
              </h3>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge?.color}`}>
                {statusBadge?.text}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <Icon name="Users" size={14} />
                <span>Up to {maxAgents} agents</span>
              </div>
              {currentPeriodEnd && (
                <div className="flex items-center gap-1.5">
                  <Icon name="Calendar" size={14} />
                  <span>Renews ACTIVE</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
          {!isFree && onManageBilling && (
            <Button
              variant="outline"
              size="md"
              onClick={onManageBilling}
              iconName="Settings"
              iconPosition="left"
              className="w-full sm:w-auto"
            >
              Manage Billing
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionBanner;